// One signature request (SIGN-FE-01, CdC SIG-01): the facts (document, revision, SHA-256, who asked,
// dates, level), the signers with their statuses, what the caller can do (receive a code and sign,
// decline, cancel their request) and, once completed, the proof (evidence certificate and integrity
// check). Expired, declined and cancelled requests say plainly what happened and what to do next.
import { ApiError } from '@batiflow/client-core';
import { ConfirmDialog, EmptyState, SectionCard, SkeletonList, isolate } from '@batiflow/ui';
import { Button, MessageBar, MessageBarBody, __css, tokens } from '@fluentui/react-components';
import { Dismiss20Regular, DocumentSignature24Regular } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { useApplyRequest, useCancelSignature, useSignatureRequest } from './api';
import { ErrorBar, HashValue, RequestStatusChip, SignerStatusChip, useWhen } from './bits';
import { EvidenceSection } from './evidence-section';
import { canCancel, canSignNow, errorMessage, mySigner, type SignatureRequest } from './model';
import { SignPanel } from './sign-panel';
import { SignaturesFrame } from './signatures-frame';

const useStyles = __css({"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":0,"Belr9w4":0,"rmohyg":"faqewft"},"facts":{"mc9l5x":"f13qh94s","Budl1dq":"f19hwdz6","i8kkvl":"f3lra4y","Belr9w4":"f1noh358","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi"},"fact":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9","Bgto7am":"fuzgk9b"},"term":{"sj55zd":"f11d4kpn","Jyy4pa":"f34sw2d"},"value":{"Jyy4pa":"f34sw2d","Btd35i7":"fjksvth"},"wide":{"Bw0ie65":0,"Br312pm":0,"Beweih1":"focznfx"},"help":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"f11d4kpn","Be2twd7":"fy9rknc"},"signers":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"signer":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja"},"signerHead":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f778t4r"},"signerName":{"Bhrd7zp":"fl43uef","Btd35i7":"fjksvth"},"line":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb","Btd35i7":"fjksvth"}});

function Fact({
  term,
  wide = false,
  children,
}: {
  term: string;
  wide?: boolean;
  children: ReactNode;
}) {
  const styles = useStyles();
  return (
    <div className={wide ? `${styles.fact} ${styles.wide}` : styles.fact}>
      <dt className={styles.term}>{term}</dt>
      <dd className={styles.value}>{children}</dd>
    </div>
  );
}

function FactsCard({ request }: { request: SignatureRequest }) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const { day, moment } = useWhen();
  const { document: doc } = request;
  return (
    <SectionCard
      title={t(request.status === 'completed' ? 'facts.titleSigned' : 'facts.title')}
      headingLevel={2}
    >
      <dl className={styles.facts}>
        <Fact term={t('facts.document')}>
          <bdi>{doc.name}</bdi>
          {' · '}
          {doc.revision ? t('facts.revision', { revision: doc.revision }) : t('facts.noRevision')}
        </Fact>
        <Fact term={t('facts.requester')}>
          <bdi>{request.requestedBy.name}</bdi>
        </Fact>
        <Fact term={t('facts.level')}>
          {t(`levels.${request.level}`)}
          <p className={styles.help}>{t(`levels.help.${request.level}`)}</p>
        </Fact>
        <Fact term={t('facts.created')}>{moment(request.createdAt)}</Fact>
        <Fact term={t('facts.expires')}>{day(request.expiresAt)}</Fact>
        {request.completedAt ? (
          <Fact term={t('facts.completed')}>{moment(request.completedAt)}</Fact>
        ) : null}
        <Fact term={t('facts.sha256')} wide>
          <HashValue hash={doc.sha256} />
          <p className={styles.help}>{t('facts.sha256Help')}</p>
        </Fact>
        {request.message ? (
          <Fact term={t('facts.message')} wide>
            <bdi>{request.message}</bdi>
          </Fact>
        ) : null}
      </dl>
    </SectionCard>
  );
}

function SignersCard({ request }: { request: SignatureRequest }) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const { moment } = useWhen();
  return (
    <SectionCard title={t('signers.title')} headingLevel={2}>
      <ol className={styles.signers} aria-label={t('signers.list')}>
        {request.signers.map((signer) => (
          <li key={signer.id} className={styles.signer}>
            <div className={styles.signerHead}>
              <span className={styles.signerName}>
                <bdi>{signer.user.name}</bdi>
                {signer.id === request.me.signerId ? ` (${t('signers.you')})` : ''}
              </span>
              <SignerStatusChip status={signer.status} />
            </div>
            {signer.signedAt ? (
              <p className={styles.line}>
                {t('signers.signedOn', { date: moment(signer.signedAt) })}
              </p>
            ) : null}
            {signer.declinedAt ? (
              <p className={styles.line}>
                {t('signers.declinedOn', { date: moment(signer.declinedAt) })}
              </p>
            ) : null}
            {signer.declineReason ? (
              <p className={styles.line}>
                {t('signers.reason', { reason: isolate(signer.declineReason) })}
              </p>
            ) : null}
            {signer.certificate ? (
              <p className={styles.line}>
                {t('signers.certificate', { subject: isolate(signer.certificate.subject) })}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </SectionCard>
  );
}

/** What the state of the request means for the caller, in plain words. */
function StateNotice({ request }: { request: SignatureRequest }) {
  const { t } = useTranslation('signatures');
  const { day, moment } = useWhen();
  const mine = mySigner(request);
  const declined = request.signers.find((signer) => signer.status === 'declined');
  const waiting = request.signers.filter((signer) => signer.status === 'pending').length;
  switch (request.status) {
    case 'completed':
      return (
        <MessageBar intent="success" layout="multiline">
          <MessageBarBody>
            {t('state.completed', { date: moment(request.completedAt ?? request.createdAt) })}
          </MessageBarBody>
        </MessageBar>
      );
    case 'declined':
      return (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>
            {t('state.declined', { name: isolate(declined?.user.name ?? '') })}
          </MessageBarBody>
        </MessageBar>
      );
    case 'cancelled':
      return (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>
            {t('state.cancelled', { name: isolate(request.requestedBy.name) })}
          </MessageBarBody>
        </MessageBar>
      );
    case 'expired':
      return (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>
            {t(request.me.isRequester ? 'state.expiredRequester' : 'state.expired', {
              date: day(request.expiresAt),
            })}
          </MessageBarBody>
        </MessageBar>
      );
    case 'pending':
      if (mine?.status === 'signed')
        return (
          <MessageBar intent="info" layout="multiline">
            <MessageBarBody>{t('state.youSigned', { count: waiting })}</MessageBarBody>
          </MessageBar>
        );
      return (
        <MessageBar intent="info" layout="multiline">
          <MessageBarBody>
            {t(request.me.canSign ? 'state.yourTurn' : 'state.waiting', {
              count: waiting,
              date: day(request.expiresAt),
            })}
          </MessageBarBody>
        </MessageBar>
      );
  }
}

function RequestContent({
  orgId,
  request,
  polling,
}: {
  orgId: string;
  request: SignatureRequest;
  polling: { waitedTooLong: boolean; restart: () => void };
}) {
  return (
    <>
      <StateNotice request={request} />
      {canSignNow(request) ? <SignPanel orgId={orgId} request={request} /> : null}
      <FactsCard request={request} />
      <SignersCard request={request} />
      {request.status === 'completed' ? (
        <EvidenceSection
          orgId={orgId}
          request={request}
          waitedTooLong={polling.waitedTooLong}
          onRestartPolling={polling.restart}
        />
      ) : null}
    </>
  );
}

export function RequestPage({ orgId, requestId }: { orgId: string; requestId: string }) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const navigate = useNavigate();
  const notify = useNotify();
  const query = useSignatureRequest(orgId, requestId);
  const cancel = useCancelSignature();
  const apply = useApplyRequest(orgId);
  const [cancelling, setCancelling] = useState(false);

  const back = () => {
    void navigate({ to: '/o/$orgId/signatures', params: { orgId } });
  };
  const crumbs = (last: string) => [{ label: t('title'), onSelect: back }, { label: last }];
  const request = query.data;

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <SignaturesFrame breadcrumb={crumbs(t('request.crumb'))}>
        {notFound ? (
          <EmptyState
            illustration={<DocumentSignature24Regular />}
            title={t('request.notFoundTitle')}
            description={t('request.notFoundBody')}
            primaryAction={{ label: t('request.back'), onClick: back }}
          />
        ) : (
          <ErrorBar
            title={t('request.loadFailedTitle')}
            body={t('request.loadFailedBody')}
            onRetry={() => {
              void query.refetch();
            }}
          />
        )}
      </SignaturesFrame>
    );
  }
  if (!request)
    return (
      <SignaturesFrame breadcrumb={crumbs(t('request.crumb'))}>
        <SkeletonList count={5} />
      </SignaturesFrame>
    );

  return (
    <SignaturesFrame
      title={request.title}
      breadcrumb={crumbs(request.title)}
      actions={
        <span className={styles.actions}>
          <RequestStatusChip status={request.status} />
          {canCancel(request) ? (
            <Button
              appearance="secondary"
              icon={<Dismiss20Regular />}
              onClick={() => {
                setCancelling(true);
              }}
            >
              {t('cancel.action')}
            </Button>
          ) : null}
        </span>
      }
    >
      <RequestContent
        orgId={orgId}
        request={request}
        polling={{ waitedTooLong: query.waitedTooLong, restart: query.restartPolling }}
      />
      <ConfirmDialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t('cancel.title', { title: isolate(request.title) })}
        body={t('cancel.body')}
        confirmLabel={t('cancel.confirm')}
        cancelLabel={t('cancel.keep')}
        destructive
        onConfirm={async () => {
          const fresh = await cancel.mutateAsync({ orgId, requestId: request.id });
          await apply(fresh);
          notify({ intent: 'success', message: t('cancel.done') });
        }}
        onError={(failure) => {
          setCancelling(false);
          notify({ intent: 'error', message: errorMessage(t, failure) });
        }}
      />
    </SignaturesFrame>
  );
}


import "/opt/batiflow/app/apps/web/src/features/signatures/request-page.griffel.css";